import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, Grid3X3, LayoutGrid, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Checkbox } from '../components/ui/checkbox';
import ProductCard from '../components/ProductCard';
import { productsApi } from '../lib/api';
import { cn } from '../lib/utils';

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState('featured');
  const [gridView, setGridView] = useState('grid-4');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const selectedCategory = searchParams.get('category') || 'all';
  const filterType = searchParams.get('filter');

  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);

  const allSizes = ['XS', 'S', 'M', 'L', 'XL', 'One Size'];
  const allColors = ['Black', 'White', 'Navy', 'Charcoal', 'Cream', 'Burgundy', 'Camel', 'Grey'];

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Build query params
        const params = {};
        if (selectedCategory && selectedCategory !== 'all') {
          params.category = selectedCategory;
        }
        if (filterType) {
          params.filter = filterType;
        }
        if (sortBy !== 'featured') {
          params.sort = sortBy;
        }

        const [productsData, categoriesData] = await Promise.all([
          productsApi.getAll(params),
          productsApi.getCategories()
        ]);
        
        setProducts(productsData);
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [selectedCategory, filterType, sortBy]);

  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    // Size filter (client-side)
    if (selectedSizes.length > 0) {
      filtered = filtered.filter(p => 
        p.sizes.some(size => selectedSizes.includes(size))
      );
    }

    // Color filter (client-side)
    if (selectedColors.length > 0) {
      filtered = filtered.filter(p => 
        p.colors.some(color => selectedColors.includes(color))
      );
    }

    return filtered;
  }, [products, selectedSizes, selectedColors]);

  const handleCategoryChange = (categoryId) => {
    const newParams = new URLSearchParams(searchParams);
    if (categoryId === 'all') {
      newParams.delete('category');
    } else {
      newParams.set('category', categoryId);
    }
    setSearchParams(newParams);
  };

  const toggleSize = (size) => {
    setSelectedSizes(prev => 
      prev.includes(size) 
        ? prev.filter(s => s !== size)
        : [...prev, size]
    );
  };

  const toggleColor = (color) => {
    setSelectedColors(prev => 
      prev.includes(color) 
        ? prev.filter(c => c !== color)
        : [...prev, color]
    );
  };

  const clearFilters = () => {
    setSelectedSizes([]);
    setSelectedColors([]);
    setSearchParams({});
  };

  const FilterContent = () => (
    <div className="space-y-8">
      {/* Categories */}
      <div>
        <h3 className="font-semibold text-sm uppercase tracking-wider text-text-primary mb-4">Category</h3>
        <div className="space-y-2">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => handleCategoryChange(category.id)}
              className={cn(
                "w-full text-left py-2 px-3 rounded-sm text-sm transition-colors",
                selectedCategory === category.id
                  ? "bg-accent-ui text-white"
                  : "text-text-subtle hover:bg-stone-100"
              )}
            >
              {category.name} ({category.count})
            </button>
          ))}
        </div>
      </div>

      {/* Sizes */}
      <div>
        <h3 className="font-semibold text-sm uppercase tracking-wider text-text-primary mb-4">Size</h3>
        <div className="flex flex-wrap gap-2">
          {allSizes.map((size) => (
            <button
              key={size}
              onClick={() => toggleSize(size)}
              className={cn(
                "px-3 py-2 text-sm border rounded-sm transition-colors",
                selectedSizes.includes(size)
                  ? "bg-accent-ui text-white border-accent-ui"
                  : "border-stone-300 text-text-subtle hover:border-accent-ui"
              )}
            >
              {size}
            </button>
          ))}
        </div>
      </div>

      {/* Colors */}
      <div>
        <h3 className="font-semibold text-sm uppercase tracking-wider text-text-primary mb-4">Color</h3>
        <div className="space-y-2">
          {allColors.map((color) => (
            <label
              key={color}
              className="flex items-center gap-3 cursor-pointer py-1"
            >
              <Checkbox
                checked={selectedColors.includes(color)}
                onCheckedChange={() => toggleColor(color)}
                className="border-stone-300"
              />
              <span className="text-sm text-text-subtle">{color}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Clear Filters */}
      {(selectedSizes.length > 0 || selectedColors.length > 0 || selectedCategory !== 'all') && (
        <Button 
          variant="outline" 
          className="w-full border-stone-300"
          onClick={clearFilters}
        >
          Clear All Filters
        </Button>
      )}
    </div>
  );

  const getPageTitle = () => {
    if (filterType === 'new') return 'New Arrivals';
    if (filterType === 'sale') return 'Sale';
    if (selectedCategory !== 'all') {
      const cat = categories.find(c => c.id === selectedCategory);
      return cat?.name || 'Products';
    }
    return 'All Products';
  };

  return (
    <main className="min-h-screen pt-20 bg-coconut">
      {/* Page Header */}
      <div className="bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          <h1 className="font-display text-4xl font-bold text-text-primary">
            {getPageTitle()}
          </h1>
          <p className="text-text-subtle mt-2">
            {filteredProducts.length} products
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Desktop Filters */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-28">
              <h2 className="font-display text-lg font-semibold text-text-primary mb-6">Filters</h2>
              <FilterContent />
            </div>
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-stone-200">
              {/* Mobile Filter Button */}
              <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
                <SheetTrigger asChild className="lg:hidden">
                  <Button variant="outline" className="border-stone-300">
                    <Filter className="h-4 w-4 mr-2" />
                    Filters
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="bg-coconut">
                  <SheetHeader>
                    <SheetTitle className="font-display text-xl">Filters</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6">
                    <FilterContent />
                  </div>
                </SheetContent>
              </Sheet>

              <div className="hidden lg:block" />

              <div className="flex items-center gap-4">
                {/* Sort */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-text-subtle hidden sm:inline">Sort by:</span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="text-sm border border-stone-300 rounded-sm px-3 py-2 bg-white focus:outline-none focus:border-accent-ui"
                  >
                    <option value="featured">Featured</option>
                    <option value="newest">Newest</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                  </select>
                </div>

                {/* Grid Toggle */}
                <div className="hidden sm:flex items-center border border-stone-300 rounded-sm">
                  <button
                    onClick={() => setGridView('grid-3')}
                    className={cn(
                      "p-2 transition-colors",
                      gridView === 'grid-3' ? "bg-accent-ui text-white" : "text-text-subtle hover:bg-stone-100"
                    )}
                  >
                    <Grid3X3 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setGridView('grid-4')}
                    className={cn(
                      "p-2 transition-colors",
                      gridView === 'grid-4' ? "bg-accent-ui text-white" : "text-text-subtle hover:bg-stone-100"
                    )}
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Products */}
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-accent-ui" />
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-text-subtle text-lg mb-4">No products found</p>
                <Button onClick={clearFilters} className="bg-accent-ui hover:bg-accent-ui/90 text-white">
                  Clear Filters
                </Button>
              </div>
            ) : (
              <div className={cn(
                "grid gap-6",
                gridView === 'grid-3' ? "grid-cols-2 md:grid-cols-3" : "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
              )}>
                {filteredProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default ShopPage;
